import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
  , signal, computed
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { fromEvent, timer, Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';

import { NavigationService } from 'src/app/services/core/navigation.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { MobileResolutionService } from 'src/app/services/platform/mobile-resolution.service';
import { MapSkin, SettingsService } from 'src/app/services/account/settings.service';
import { LanguageService } from 'src/app/services/account/language.service';
import { WebviewService } from 'src/app/services/platform/webview.service';
import { MapService } from 'src/app/services/map/map.service';
import { StackedModalsService } from 'src/app/services/modal/stacked-modals.service';
import { WaypointService } from 'src/app/services/core/waypoint.service';
import { ConfirmationDialogComponent } from '../../modals/confirmation-dialog/confirmation-dialog.component';
import { NavigationSetBatteryDialogComponent } from '../../modals/navigation-set-battery-dialog/navigation-set-battery-dialog.component';
import { Computer } from 'src/app/models/computer';
import { RoutePlanElement } from 'src/app/models/route';
import { ChargeState } from 'src/app/models/charge-state';
import { BetterRouteState } from 'src/app/models/better-route';
import { NavigationPlanHeaderComponent } from './navigation-plan-header/navigation-plan-header.component';
import { NavigationPlanNormalStateComponent } from './navigation-plan-normal-state/navigation-plan-normal-state.component';
import { NavigationPlanChargeStateComponent } from './navigation-plan-charge-state/navigation-plan-charge-state.component';
import { NavigationPlanBetterRouteComponent } from './navigation-plan-better-route/navigation-plan-better-route.component';

@Component({
  standalone: true,
  selector: 'app-navigation-plan',
  templateUrl: './navigation-plan.component.html',
  styleUrls: ['./navigation-plan.component.scss'],
  imports: [
    CommonModule,
    NavigationPlanHeaderComponent,
    NavigationPlanNormalStateComponent,
    NavigationPlanChargeStateComponent,
    NavigationPlanBetterRouteComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavigationPlanComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('paneDragOuter', { static: false }) paneDragOuter!: ElementRef<HTMLElement>;
  @ViewChild('npCard', { static: false }) npCard!: ElementRef<HTMLElement>;

  /** How long the better-route offer stays on screen before dismissing itself.
   *  Also drives the countdown strip in the prompt, so the two stay in sync. */
  public readonly betterRouteTimeoutMs = 10000;

  public readonly hudBottomOpened = signal<boolean>(false);
  public readonly appliedHeight = signal<number | null>(null);
  public readonly isDragging = signal<boolean>(false);
  public readonly hasTurn = signal<boolean>(false);
  public readonly speedLimit = signal<number>(0);
  public readonly computersArray = signal<Computer[]>([]);
  public readonly roadLanesVisible = signal<boolean>(false);
  public readonly laneInfoCount = signal<number>(0);
  public readonly floatingInfoVisible = signal<boolean>(true);
  public readonly estimatedBatteryLevel = signal<number>(100);
  public readonly NextWaypoint = signal<boolean>(false);
  public readonly NextWaypointDisplayName = signal<string>('');
  public readonly CannotSkipMoreChargingStation = signal<boolean>(false);
  public readonly mobileVisiblePanel = signal<string>('');
  public readonly arrivedChargerDetails = signal<RoutePlanElement | null>(null);
  public readonly arrivedChargerIsEndpoint = signal<boolean>(false);
  public readonly chargeState = signal<ChargeState>(ChargeState.None);
  public ChargeState = ChargeState;
  public readonly chargeTime = signal<string>('');
  public readonly chargerIconUrl = signal<string>('assets/icons/dark/turn/charge.svg');
  public readonly arrivedAtMessage = signal<string>('');
  public readonly betterRouteMessage = signal<string>('');

  // The charging stop owns the card while it is active, so a better-route offer
  // arriving mid-charge never takes it over.
  public readonly betterRouteVisible = computed<boolean>(
    () => this.betterRouteMessage() != '' && this.chargeState() == ChargeState.None
  );

  public NavigationPlanDialogRef;

  private readonly destroyRef = inject(DestroyRef);
  private navigation: boolean = true;
  private betterRouteTimeoutSub: Subscription | null = null;

  constructor(
    public navigationService: NavigationService,
    private cdr: ChangeDetectorRef,
    private utilsService: UtilsService,
    public mobileResolutionService: MobileResolutionService,
    public settingsService: SettingsService,
    public languageService: LanguageService,
    public webviewService: WebviewService,
    public mapService: MapService,
    private snackBar: MatSnackBar,
    private stackedModalsService: StackedModalsService,
    private waypointService: WaypointService
  ) { }

  ngOnInit(): void {
    this.navigationService.ObservableShowRoadLane.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp != null) {
        this.roadLanesVisible.set(resp);
      }
    });

    this.navigationService.ObservableNextTurn.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp && this.navigation) {
        this.hasTurn.set(!!resp.NextTurnInstruction);
        this.laneInfoCount.set(resp.NextTurnInstruction ? resp.NextTurnInstruction.LaneInfo.length : 0);
        this.estimatedBatteryLevel.set(resp.EstimatedBatteryLevel);

        let nextWpChComputer: Computer | null = null;

        if (resp.DistToNextChargingStation != null && resp.DistToNextWaypoint != null) {
          if (resp.DistToNextWaypoint < resp.DistToNextChargingStation) {
            nextWpChComputer = new Computer('wp', '/assets/icons/' + this.settingsService.getMap().Skin + '/ui/computer_icon_waypoint.svg', resp.DistToNextWaypoint, this.utilsService.timeConverter(resp.TimeToNextWaypoint!), resp.NextWaypointBatteryReservedWarning);
            this.NextWaypointDisplayName.set(resp.NextWaypointDisplayName);
          } else {
            nextWpChComputer = new Computer('cs', '/assets/icons/' + this.settingsService.getMap().Skin + '/ui/chargingplan_icon.svg', resp.DistToNextChargingStation, this.utilsService.timeConverter(resp.TimeToNextChargingStation!), resp.NextChargingStationBatteryReservedWarning);
          }
        } else if (resp.DistToNextWaypoint != null) {
          nextWpChComputer = new Computer('wp', '/assets/icons/' + this.settingsService.getMap().Skin + '/ui/computer_icon_waypoint.svg', resp.DistToNextWaypoint, this.utilsService.timeConverter(resp.TimeToNextWaypoint!), resp.NextWaypointBatteryReservedWarning);
          this.NextWaypointDisplayName.set(resp.NextWaypointDisplayName);
        } else if (resp.DistToNextChargingStation != null) {
          nextWpChComputer = new Computer('cs', '/assets/icons/' + this.settingsService.getMap().Skin + '/ui/chargingplan_icon.svg', resp.DistToNextChargingStation, this.utilsService.timeConverter(resp.TimeToNextChargingStation!), resp.NextChargingStationBatteryReservedWarning);
        }

        const destinationComputer: Computer = new Computer('t', '/assets/icons/' + this.settingsService.getMap().Skin + '/ui/target_icon.svg', resp.DistToDest, this.utilsService.timeConverter(resp.TimeToDest), resp.DestBatteryReservedWarning);

        // Rebuilt on every update instead of being mutated in place. The previous version
        // push/unshift-ed into the existing array and copied values onto the existing Computer
        // objects, so the array reference never changed: the child holding it as an @Input was
        // never told anything happened, and the pills stayed empty until some unrelated event
        // re-rendered it. (That path also dropped the next-waypoint row on the first update,
        // because it overwrote index 0 with the destination while the array still had one entry.)
        this.computersArray.set(nextWpChComputer != null
          ? [nextWpChComputer, destinationComputer]
          : [destinationComputer]);

        this.speedLimit.set(resp.SpeedLimit);
      }
      if (resp && resp.DistToNextWaypoint != null) {
        this.NextWaypointDisplayName.set(resp.NextWaypointDisplayName);
      }
    });

    this.navigationService.ObservableCannotSkipMoreChargingStation.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp) {
        this.CannotSkipMoreChargingStation.set(resp);
      }
    });

    this.mobileResolutionService.ObservableMobileVisiblePanel.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((panelName) => {
      if (panelName != undefined) {
        this.mobileVisiblePanel.set(panelName);
      }
    });

    this.navigationService.ObservableFloatingInfoVisible.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp != null) {
        this.floatingInfoVisible.set(resp);
      }
    });

    this.navigationService.ObservableArrivedToCharger.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp) {
        const waypoints = this.waypointService.getWaypointsParams();
        if (waypoints[waypoints.length - 1].csid && resp.ChargingStationId == waypoints[waypoints.length - 1].csid) {
          this.arrivedChargerIsEndpoint.set(true);
        } else {
          this.arrivedChargerIsEndpoint.set(false);
        }

        this.arrivedChargerDetails.set(resp);
        this.chargeTime.set(this.utilsService.getChargeTime(
          this.arrivedChargerDetails()!.ChargeTimeHour,
          this.arrivedChargerDetails()!.ChargeTimeMin
        ));

        this.arrivedAtMessage.set(this.languageService.languageJSON.NavigationHud_ArrivedAtCharger
          .replace('$chargername', '<b>' + this.arrivedChargerDetails()?.Name + '</b>'));
      }
    });

    this.navigationService.ObservableChargeStateChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp != undefined && resp != null) {
        this.chargeState.set(resp);
      }
    });

    this.navigationService.ObservableBetterRoute.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp && resp.state == BetterRouteState.ShowPopup) {
        this.betterRouteMessage.set(this.languageService.languageJSON.NavigationHud_ReplanMessage
          .replace('$minuteDifference', String(resp.minutesDifference)));

        // Drop any height left over from a drag so the card can grow to fit the
        // offer instead of staying pinned at the collapsed height.
        this.appliedHeight.set(null);

        // When the countdown strip runs out the offer expires on its own and the
        // card returns to the normal navigation rows.
        this.betterRouteTimeoutSub?.unsubscribe();
        this.betterRouteTimeoutSub = timer(this.betterRouteTimeoutMs)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.dismissBetterRoute());
      } else if (!resp) {
        // Cleared by the calculation flow (navigation stopped, route replanned).
        this.clearBetterRoute();
      }
    });

    this.settingsService.ObservableMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp) {
        if (resp.Skin == MapSkin.Dark) {
          this.chargerIconUrl.set('assets/icons/dark/ui/charge_accent_outline.svg');
        }
        if (resp.Skin == MapSkin.Light) {
          this.chargerIconUrl.set('assets/icons/dark/turn/charge.svg');
        }
      }
    });
  }

  public toggleBottomPanel(): void {
    this.hudBottomOpened.set(!this.hudBottomOpened());
  }

  ngAfterViewInit(): void {
    this.initPaneEvents();

    // Flag navigation as active so the floating map buttons (voice, sliders,
    // follow-mode) can lift themselves above this card, and expose the card's
    // height for that offset.
    document.body.classList.add('nav-plan-active');
    this.publishCardHeight();
    fromEvent(window, 'resize')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.publishCardHeight());
  }

  private publishCardHeight(): void {
    if (!this.npCard) {
      return;
    }
    // Measured in the collapsed state (the default), so the buttons stay put
    // just above the card and are simply covered when the user expands it.
    const height = this.npCard.nativeElement.offsetHeight;
    document.documentElement.style.setProperty('--nav-card-height', `${height}px`);
  }

  private initPaneEvents(): void {
    if (!this.npCard) {
      return;
    }

    const cardEl = this.npCard.nativeElement;

    const isInteractive = (target: EventTarget | null): boolean => {
      if (!target || !(target instanceof Element)) {
        return false;
      }
      return !!target.closest('.np-actions, button, a, .battery-reserved-warning-icon');
    };

    let closedHeight = 0;
    let openedHeight = 0;
    let startHeight = 0;
    let clearHeightTimer: any = null;
    // Suppress the emulated mouse events (mousedown/mouseup) browsers fire a few
    // hundred ms after a touch tap — otherwise every tap toggles twice and the
    // card visibly jumps back to its original state.
    let lastTouchAt = 0;

    const measureHeights = (): void => {
      // Measure both states by toggling. detectChanges renders synchronously before any
      // browser paint, so this is invisible to the user. These calls must stay even though
      // the state is signal-based: a signal write only schedules a tick, it does not render.
      const originalOpened = this.hudBottomOpened();
      const originalApplied = this.appliedHeight();
      this.appliedHeight.set(null);

      this.hudBottomOpened.set(true);
      this.cdr.detectChanges();
      openedHeight = cardEl.offsetHeight;

      this.hudBottomOpened.set(false);
      this.cdr.detectChanges();
      closedHeight = cardEl.offsetHeight;

      this.hudBottomOpened.set(originalOpened);
      this.appliedHeight.set(originalApplied);
      this.cdr.detectChanges();
    };

    // During an active drag we write the height straight to the DOM instead of
    // going through Angular change detection on every move event — that keeps
    // the gesture smooth (no per-frame CD churn on the card + its children).
    const applyHeightDirect = (h: number): void => {
      if (h < closedHeight) h = closedHeight;
      if (h > openedHeight) h = openedHeight;
      cardEl.style.height = `${h}px`;
    };

    const snapTo = (opened: boolean): void => {
      this.hudBottomOpened.set(opened);
      this.isDragging.set(false);
      const target = opened ? openedHeight : closedHeight;
      // Keep an explicit height during the transition so the animation works.
      this.appliedHeight.set(target);
      // Force the final height onto the element even when the bound value did
      // not change (e.g. a below-threshold drag that snaps back to its start):
      // Angular skips the write in that case, which would otherwise leave the
      // card stuck at the dragged-to height.
      cardEl.style.height = `${target}px`;

      if (clearHeightTimer) {
        clearTimeout(clearHeightTimer);
      }
      clearHeightTimer = setTimeout(() => {
        this.appliedHeight.set(null);
        clearHeightTimer = null;
      }, 350);
    };

    const beginDrag = (target: EventTarget | null): boolean => {
      if (isInteractive(target)) {
        return false;
      }
      if (clearHeightTimer) {
        clearTimeout(clearHeightTimer);
        clearHeightTimer = null;
      }
      // Capture the height as it is rendered right now (possibly mid-transition)
      // so grabbing the card while it animates continues smoothly from that
      // point instead of snapping to the collapsed/expanded height.
      const currentHeight = cardEl.offsetHeight;
      measureHeights();
      startHeight = currentHeight;
      this.isDragging.set(true);
      this.appliedHeight.set(startHeight);
      cardEl.style.height = `${startHeight}px`;
      return true;
    };

    const endDrag = (delta: number): void => {
      // Tap (negligible movement) → toggle
      if (Math.abs(delta) < 5) {
        snapTo(!this.hudBottomOpened());
        return;
      }
      const threshold = 30;
      if (this.hudBottomOpened()) {
        // dragging down (positive delta) closes
        snapTo(delta > threshold ? false : true);
      } else {
        // dragging up (negative delta) opens
        snapTo(delta < -threshold ? true : false);
      }
    };

    // --------------- Mouse drag ---------------
    let startYMouse = 0;
    let lastDeltaMouse = 0;
    let mouseActive = false;

    fromEvent<MouseEvent>(cardEl, 'mousedown').pipe(takeUntilDestroyed(this.destroyRef)).subscribe((md: MouseEvent) => {
      // Ignore the synthetic mouse event that follows a real touch tap.
      if (Date.now() - lastTouchAt < 700) return;
      if (!beginDrag(md.target)) return;
      startYMouse = md.pageY;
      lastDeltaMouse = 0;
      mouseActive = true;
    });

    fromEvent<MouseEvent>(document, 'mousemove').pipe(takeUntilDestroyed(this.destroyRef)).subscribe((mm: MouseEvent) => {
      if (!mouseActive) return;
      const delta = mm.pageY - startYMouse;
      lastDeltaMouse = delta;
      // drag up (negative delta) → larger height
      applyHeightDirect(startHeight - delta);
    });

    fromEvent<MouseEvent>(document, 'mouseup').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!mouseActive) return;
      mouseActive = false;
      endDrag(lastDeltaMouse);
    });

    // --------------- Touch drag ---------------
    let startYTouch = 0;
    let lastDeltaTouch = 0;
    let touchActive = false;

    fromEvent<TouchEvent>(cardEl, 'touchstart', { passive: true } as any).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((ts: TouchEvent) => {
      lastTouchAt = Date.now();
      if (!beginDrag(ts.target)) return;
      startYTouch = ts.changedTouches[0].pageY;
      lastDeltaTouch = 0;
      touchActive = true;
    });

    fromEvent<TouchEvent>(cardEl, 'touchmove', { passive: true } as any).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((tm: TouchEvent) => {
      if (!touchActive) return;
      const delta = tm.changedTouches[0].pageY - startYTouch;
      lastDeltaTouch = delta;
      applyHeightDirect(startHeight - delta);
    });

    fromEvent<TouchEvent>(cardEl, 'touchend').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      lastTouchAt = Date.now();
      if (!touchActive) return;
      touchActive = false;
      endDrag(lastDeltaTouch);
    });
  }

  public skipWaypoint(): void {
    let skipmessage = '';
    if (this.NextWaypointDisplayName() != null && this.NextWaypointDisplayName() != undefined) {
      skipmessage = this.languageService.languageJSON.NavigationHud_SkipWaypointWithName_Message
        .replace(`$waypoint`, `<b>${this.NextWaypointDisplayName()}</b>`);
    } else {
      skipmessage = this.languageService.languageJSON.NavigationHud_SkipWaypoint_Message;
    }

    this.NavigationPlanDialogRef = this.stackedModalsService.openModal(ConfirmationDialogComponent, {
      data: {
        title: this.languageService.languageJSON.NavigationHud_SkipWaypoint,
        message: skipmessage,
        buttons: [this.languageService.languageJSON.Global_Skip, this.languageService.languageJSON.Global_Cancel]
      },
      autoFocus: false
    });

    this.NavigationPlanDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.navigationService.SkipNextWaypoint();
      }
      this.NavigationPlanDialogRef = null;
    });
  }

  public skipChargingStation(): void {
    const skipmessage = this.languageService.languageJSON.NavigationHud_SkipChargingStation_Message;

    this.NavigationPlanDialogRef = this.stackedModalsService.openModal(ConfirmationDialogComponent, {
      data: {
        title: this.languageService.languageJSON.NavigationHud_SkipChargingStation,
        message: skipmessage,
        buttons: [this.languageService.languageJSON.Global_Skip, this.languageService.languageJSON.Global_Cancel]
      },
      autoFocus: false
    });

    this.NavigationPlanDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.navigationService.SkipNextChargingStation();
      }
      this.NavigationPlanDialogRef = null;
    });
  }

  public replanNavigation(): void {
    this.navigationService.SkipNextWaypoint();
  }

  /** Accept the shorter route: hands the offer back as a replan request. */
  public acceptBetterRoute(): void {
    const betterRouteValue = this.navigationService.ObservableBetterRoute.value;
    if (betterRouteValue) {
      betterRouteValue.state = BetterRouteState.Replan;
      this.navigationService.ObservableBetterRoute.next(betterRouteValue);
    }
    this.clearBetterRoute();
  }

  /** Declined or expired: the card goes back to the normal navigation rows. */
  public dismissBetterRoute(): void {
    // Drop the offer itself, not just the message. ObservableBetterRoute is a
    // BehaviorSubject, so leaving it at ShowPopup would replay the prompt into
    // a rebuilt card (panel switch, navigation restart).
    if (this.navigationService.ObservableBetterRoute.value) {
      this.navigationService.ObservableBetterRoute.next(null);
    }
    this.clearBetterRoute();
  }

  private clearBetterRoute(): void {
    this.betterRouteTimeoutSub?.unsubscribe();
    this.betterRouteTimeoutSub = null;
    this.betterRouteMessage.set('');
  }

  public startCharge(): void {
    this.navigationService.setChargeState(ChargeState.Charging);
  }

  public finishCharge(): void {
    this.navigationService.setChargeState(ChargeState.ChargeFinish);
    if (this.arrivedChargerDetails()) {
      this.NavigationPlanDialogRef = this.stackedModalsService.openModal(NavigationSetBatteryDialogComponent, {
        data: {
          chargedBatteryLevel: this.arrivedChargerDetails()!.BatteryMaxPercent,
          chargingStationId: this.arrivedChargerDetails()!.ChargingStationId,
          arrivedChargerIsEndpoint: this.arrivedChargerIsEndpoint()
        },
        autoFocus: false
      });
    }

    this.NavigationPlanDialogRef.afterClosed().subscribe(() => {
      if (this.arrivedChargerIsEndpoint()) {
        this.navigationService.ObservableChangeNavigation.next('arrive');
        this.navigationService.setChargeState(ChargeState.None);
      }
      this.NavigationPlanDialogRef = null;
    });
  }

  public stopNavigation(): void {
    this.NavigationPlanDialogRef = this.stackedModalsService.openModal(ConfirmationDialogComponent, {
      data: {
        title: this.languageService.languageJSON.NavigationHud_StopNavigation,
        message: this.languageService.languageJSON.NavigationHud_StopNavigation_Message,
        buttons: [this.languageService.languageJSON.Global_Stop, this.languageService.languageJSON.Global_Cancel]
      },
      autoFocus: false
    });

    this.NavigationPlanDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.navigationService.ObservableChangeNavigation.next('exit');
      }
      this.NavigationPlanDialogRef = null;
    });
  }

  public openBatteryReservedSnackbar(): void {
    this.snackBar.open(this.languageService.languageJSON.WarningSoc_popup, '', {
      duration: 5000,
      panelClass: 'snackbar-warning'
    });
  }

  ngOnDestroy(): void {
    this.betterRouteTimeoutSub?.unsubscribe();
    if (this.NavigationPlanDialogRef) {
      this.NavigationPlanDialogRef.close();
    }
    document.body.classList.remove('nav-plan-active');
    document.documentElement.style.removeProperty('--nav-card-height');
  }
}
